import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { Formik, useFormikContext, getIn, setIn } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Button } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import { FarmScheduleCoverageType } from '@ourbranch/lookups';
import { getDaysBetweenDates } from '@ourbranch/date-fns-helpers';
import Grid from '@material-ui/core/Grid';

import { SubRiskCollapsibleSection } from './sub-risk-collapsible-section';
import { AppliedTag } from 'common/components/farm/applied-tag';
import { appConfig } from 'brand-config';
import Field from 'core/components/form/form.v2';
import FormSectionWrapper from 'common/components/form-section-wrapper';
import withDatePicker from 'core/components/with-date-picker';
import { LabelTooltip } from 'core/components/label-tooltip';
import CollapsibleListItem from './collapsible-list-item';
import {
  ValidationErrorsModal,
  useValidationErrorsModal,
  setTouchedFromErrors
} from 'core/components/validation-errors-modal';
import { getCategoryDisplayValue, getFilteredPeakSeasonCategories } from './peak-season-inventory.helpers';

import useStyles from './sub-risk.styles';
import { hasErrorMessages } from './sub-risk.utils';

// Date-only strings ('YYYY-MM-DD') parse as midnight UTC, which renders as the PREVIOUS
// calendar day in negative-UTC-offset timezones (US Central/Eastern) — anchor them to
// local noon so the picker bounds match the calendar date the user entered.
const parseDateOnlyAsLocalNoon = (value) =>
  typeof value === 'string' && !value.includes('T') ? new Date(`${value}T12:00:00`) : new Date(value);

const validationSchema = () =>
  Yup.object().shape({
    category: Yup.string().required('Type of item is required'),
    name: Yup.string().required('Name is required'),
    limit: Yup.number().required('Coverage limit is required'),
    startDate: Yup.date().required('Start date is required'),
    endDate: Yup.date().required('End date is required').min(Yup.ref('startDate'), 'End date must be after start date')
  });

const PeakSeasonInventoryForm = ({ fieldName, disabled, state, scheduledItems, policyStartDate, policyEndDate }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched, validateForm, setErrors, setTouched } = useFormikContext();
  const { showValidationErrorsModal, setShowValidationErrorsModal } = useValidationErrorsModal();

  // Peak season periods run within the policy term, so dates are selectable from the policy
  // effective date (which a UW may set in the past or future) — not from today (#31677).
  const termStartDate = policyStartDate ? parseDateOnlyAsLocalNoon(policyStartDate) : new Date();
  const termEndDate = policyEndDate ? parseDateOnlyAsLocalNoon(policyEndDate) : undefined;

  const scheduleCoverages = getIn(values, fieldName) || [];
  const peakSeasonItems =
    scheduleCoverages.find((item) => item.coverageType === FarmScheduleCoverageType.PEAK)?.items || [];
  const coverageIndex = scheduleCoverages.findIndex((c) => c.coverageType === FarmScheduleCoverageType.PEAK);

  const allPeakCategories =
    appConfig?.farm[state]?.risks?.FO6Scheduled?.allowedOptions?.peakSeasonItemTypes ||
    lookupsJson.farm.scheduledFarmPropertyItemTypesByState[state];

  const scheduledItemCategories = scheduledItems?.map((item) => item.category);
  const availableCategories = getFilteredPeakSeasonCategories(allPeakCategories, scheduledItemCategories);

  return (
    <>
      <ValidationErrorsModal open={showValidationErrorsModal} onClose={() => setShowValidationErrorsModal(false)} />
      <SubRiskCollapsibleSection
        type="secondary"
        content={<div className={classes.headerText}>Peak Season Inventory</div>}
        contentRight={peakSeasonItems.length > 0 ? <AppliedTag /> : null}
        initialOpen={false}
      >
        <FormSectionWrapper
          sectionTitle="Peak season inventory items"
          totalLabel="items"
          totalCount={peakSeasonItems?.length || 0}
          emptyStateTitle="There are no peak season inventory items added yet"
        >
          {/* list items */}
          {peakSeasonItems.map((item, index) => {
            const itemCategory = getCategoryDisplayValue(item.category, allPeakCategories);
            const itemPath = `${fieldName}[${coverageIndex}].items[${index}]`;
            const startDate = item.extendedDetails?.startDate;
            const endDate = item.extendedDetails?.endDate;
            const computedDays =
              startDate && endDate ? getDaysBetweenDates(new Date(startDate), new Date(endDate))?.toString() : 'N/A';

            const syncDays = (newStart, newEnd) => {
              if (newStart && newEnd) {
                const days = getDaysBetweenDates(new Date(newStart), new Date(newEnd));
                setFieldValue(`${itemPath}.extendedDetails.days`, days);
              } else {
                setFieldValue(`${itemPath}.extendedDetails.days`, null);
              }
            };
            const handleStartDateChange = (date) => syncDays(date?.value, endDate);
            const handleEndDateChange = (date) => syncDays(startDate, date?.value);

            return (
              <CollapsibleListItem
                key={`peakSeasonInventory-${index}`}
                title={item.extendedDetails?.name || itemCategory}
                rightSideValues={[{ label: 'Limit', value: `$${item.limit?.toLocaleString('en-US') || item.limit}` }]}
                onDelete={() => {
                  const newItems = peakSeasonItems.filter((_, checkIndex) => checkIndex !== index);
                  const currentScheduleCoverages =
                    getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.PEAK) ||
                    [];
                  setFieldValue(`${fieldName}`, [
                    ...currentScheduleCoverages,
                    {
                      coverageType: FarmScheduleCoverageType.PEAK,
                      items: newItems
                    }
                  ]);
                  setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.PEAK}`);
                }}
                disabled={disabled}
              >
                <Field
                  xs={4}
                  type="string"
                  id={`${itemPath}.extendedDetails.name`}
                  name={`${itemPath}.extendedDetails.name`}
                  mode="dark"
                  label="Name"
                  disabled={disabled}
                />
                <Field
                  xs={4}
                  type="numeric"
                  id={`${itemPath}.limit`}
                  name={`${itemPath}.limit`}
                  mode="dark"
                  label="Amount of increase"
                  format={{ thousandSeparator: true, prefix: '$' }}
                  disabled={disabled}
                />
                <Field
                  xs={4}
                  type="date"
                  id={`${itemPath}.extendedDetails.startDate`}
                  name={`${itemPath}.extendedDetails.startDate`}
                  mode="dark"
                  label="Start date"
                  disabled={disabled}
                  defaultToEmpty
                  disableFuture={false}
                  minDate={termStartDate}
                  maxDate={termEndDate}
                  onChange={handleStartDateChange}
                />
                <Field
                  xs={4}
                  type="date"
                  id={`${itemPath}.extendedDetails.endDate`}
                  name={`${itemPath}.extendedDetails.endDate`}
                  mode="dark"
                  label="End date"
                  disabled={disabled}
                  defaultToEmpty
                  disableFuture={false}
                  minDate={startDate ? parseDateOnlyAsLocalNoon(startDate) : termStartDate}
                  maxDate={termEndDate}
                  onChange={handleEndDateChange}
                />
                <Field
                  xs={4}
                  type="value"
                  id={`${itemPath}-days-display`}
                  name={`${itemPath}-days-display`}
                  mode="dark"
                  label="Period of time"
                  formatter={() => computedDays}
                />
              </CollapsibleListItem>
            );
          })}

          {/* add form */}
          <div className={classes.addFormContainer}>
            <Formik
              initialValues={{
                category: undefined,
                name: undefined,
                limit: undefined,
                startDate: undefined,
                endDate: undefined,
                days: 'N/A',
                description: undefined
              }}
              validateOnChange={false}
              validateOnBlur={false}
              validationSchema={validationSchema()}
              onSubmit={async (val, { resetForm }) => {
                const parentErrors = await validateForm();
                // Exclude scheduleCoverages error since adding this item is what resolves it (#29771)
                const errorsExcludingScheduleCoverages = setIn(parentErrors, fieldName, undefined);
                if (hasErrorMessages(errorsExcludingScheduleCoverages)) {
                  setErrors(errorsExcludingScheduleCoverages);
                  setTouched(setTouchedFromErrors(errorsExcludingScheduleCoverages), false);
                  setShowValidationErrorsModal(true);
                  return;
                }

                const item = val;
                Object.keys(item).forEach((key) => {
                  if (item[key] === '') {
                    item[key] = null;
                  }
                });

                const newItem = {
                  category: item.category,
                  limit: item.limit,
                  extendedDetails: {
                    name: item.name,
                    startDate: item.startDate,
                    endDate: item.endDate,
                    days: getDaysBetweenDates(new Date(item.startDate), new Date(item.endDate))
                  }
                };

                const currentScheduleCoverages =
                  getIn(values, fieldName)?.filter((item) => item.coverageType !== FarmScheduleCoverageType.PEAK) || [];
                const currentPeakSeasonItems =
                  getIn(values, fieldName)?.find((item) => item.coverageType === FarmScheduleCoverageType.PEAK)
                    ?.items || [];

                setFieldValue(`${fieldName}`, [
                  ...currentScheduleCoverages,
                  {
                    coverageType: FarmScheduleCoverageType.PEAK,
                    items: [...currentPeakSeasonItems, newItem]
                  }
                ]);
                setFieldTouched(`${fieldName}.${FarmScheduleCoverageType.PEAK}`);

                resetForm();
              }}
            >
              {({ values: formValues, setFieldValue: setFormFieldValue, submitForm }) => {
                // Calculate number of days whenever start or end date changes
                useEffect(() => {
                  if (formValues.startDate && formValues.endDate) {
                    const days = getDaysBetweenDates(new Date(formValues.startDate), new Date(formValues.endDate));
                    setFormFieldValue('days', days.toString());
                  } else {
                    setFormFieldValue('days', 'N/A');
                  }
                }, [formValues.startDate, formValues.endDate, setFormFieldValue]);

                return (
                  <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    <Grid item xs={4}>
                      <LabelTooltip
                        mode="dark"
                        label="Type of item"
                        tooltip={{
                          label: 'More Info',
                          onHoverText:
                            'Peak Season Inventory provides increased limits for hay, grain, or other scheduled items during high-inventory periods. Those items must already be scheduled on the policy.'
                        }}
                      >
                        <Field
                          type="select"
                          id="category"
                          name="category"
                          mode="dark"
                          options={availableCategories}
                          disabled={disabled}
                        />
                      </LabelTooltip>
                    </Grid>
                    <Field xs={4} type="string" id="name" name="name" mode="dark" label="Name" disabled={disabled} />
                    <Field
                      xs={4}
                      type="numeric"
                      id="coverageLimit"
                      name="limit"
                      mode="dark"
                      label="Amount of increase"
                      format={{ thousandSeparator: true, prefix: '$' }}
                      disabled={disabled}
                    />

                    <Field
                      xs={4}
                      type="date"
                      id="startDate"
                      name="startDate"
                      mode="dark"
                      label="Start date"
                      disabled={disabled}
                      defaultToEmpty
                      disableFuture={false}
                      minDate={termStartDate}
                      maxDate={termEndDate}
                    />
                    <Field
                      xs={4}
                      type="date"
                      id="endDate"
                      name="endDate"
                      mode="dark"
                      label="End date"
                      disabled={disabled}
                      defaultToEmpty
                      disableFuture={false}
                      minDate={formValues.startDate ? parseDateOnlyAsLocalNoon(formValues.startDate) : termStartDate}
                      maxDate={termEndDate}
                    />
                    <Field
                      xs={4}
                      type="value"
                      id="days"
                      name="days"
                      mode="dark"
                      label="Period of time"
                      disabled={true}
                    />

                    <Grid container alignItems="center" className={classes.singleBottom}>
                      <Button
                        type="button"
                        mode="big"
                        variant="contained"
                        color="secondary"
                        className={classes.button}
                        disabled={disabled}
                        onClick={submitForm}
                      >
                        Add Item
                      </Button>
                    </Grid>
                  </Grid>
                );
              }}
            </Formik>
          </div>
        </FormSectionWrapper>
      </SubRiskCollapsibleSection>
    </>
  );
};

PeakSeasonInventoryForm.propTypes = {
  fieldName: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  scheduledItems: PropTypes.array,
  policyStartDate: PropTypes.string,
  policyEndDate: PropTypes.string
};

PeakSeasonInventoryForm.defaultProps = {
  disabled: false
};

export default withDatePicker(observer(PeakSeasonInventoryForm));
